
import { SrsPlaceVoteComponent } from '../../srs-place-vote/srs-place-vote.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { debounceTime, distinctUntilChanged, filter, Observable, Subject, switchMap, take, takeUntil, takeWhile } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { SrsBallotGuidanceComponent } from '../srs-ballot-guidance/srs-ballot-guidance.component';
import { FileDownloadUtility } from '../../../../../shared/utilities/blob-download-utility';
import { materialSymbols } from '../../../../../shared/constants/material-symbols';
import { SRSBallotStatus } from '../../../../../shared/constants/srs-ballot.constants';
import { UnifiedBallotStatus } from '../../../../../shared/enums/unified-ballot-status-enum';
import { IApiResponse } from '../../../../../shared/models/api-response';
import { IUser } from '../../../../../shared/models/user';
import { AuthService } from '../../../../../shared/services/auth.service';
import { SamlService } from '../../../../../shared/services/saml.service';
import { urlUtility } from '../../../../../shared/utilities/url-utility';
import { UserUtility } from '../../../../../shared/utilities/user-utility';
import { DateFormatPipe } from '../../../../controls/pipes/date-format.pipe';
import { SummaryComponentView } from '../../../shared/components/button-vote-now/button-vote-now.component';
import { ISrUpdateNoteRequest } from '../../../shared/models/sr/sr-update-note-request';
import { ISrsBallotDetail, SrsBallotStatusId } from '../../../shared/models/sr/srs-ballot-details';
import { ISrsBallotUpdateDatesRequest } from '../../../shared/models/sr/srs-update-dates-request';
import { ISrsBallotVote, SrsVoteId, srsVoteOption } from '../../../shared/models/sr/srs-vote';
import { DocumentService } from '../../../../sa-library/shared/services/document.service';
import { SrsBallotService } from '../../../shared/services/srs-ballot.service';


@Component({
    selector: 'app-srs-summary',
    templateUrl: './srs-summary.component.html',
    styleUrl: './srs-summary.component.scss',
    standalone: false
})
export class SrsSummaryComponent implements OnInit, OnDestroy {

  unifiedBallotStatus = UnifiedBallotStatus;

  @Input() srsBallotDetail: ISrsBallotDetail | null = null;

  @Output() onPlaceVote = new EventEmitter<string>();
  @Output() afterSave = new EventEmitter<string>();


  isBallotStartDateEditable: boolean = false;
  isBallotEndDateEditable: boolean = false;
  ballotStartDate: Date | undefined = undefined;
  ballotEndDate: Date | undefined = undefined;
  ballotStartMinDate: Date = new Date();
  ballotStartMaxDate: Date = new Date();
  ballotEndMinDate: Date = new Date();
  ballotEndMaxDate: Date = new Date();
  ballotId: string = '';
  ballotSummaryDateForm: FormGroup = new FormGroup({
    ballotStartDateCtrl: new FormControl(),
    ballotEndDateCtrl: new FormControl()
  });

  isDisabled: boolean = true;
  currentUser: IUser | null = null;
  isUserPmOrAbove: boolean = false;
  isAuthorized: boolean = false;
  srsBallotStatus: string = '';
  ballotStatusId: UnifiedBallotStatus = UnifiedBallotStatus.created;
  srsBallotUpdateDatesRequest: ISrsBallotUpdateDatesRequest = {
    ballotId: '',
    startDate: null,
    endDate: null
  };

  ballotNoteFormGroup = new FormGroup({
    ballotNoteCtrl: new FormControl<string | null>(null),
  });

  userVote: ISrsBallotVote | undefined;
  ballotVotes: ISrsBallotVote[] = [];
  isJoint: boolean = false;
  isVoteButtonEnable: boolean = false;
  hasVoted: boolean = false;
  isVotingActionsVisible: boolean = false;
  ballotResolution: string = '';
  assignTo: string = '';
  openVotePopup: boolean = false;
  designation: string = '';
  designationURL: string = '';
  isInternalPublicUser: boolean = false;

  withdraw = materialSymbols.logout;
  reconfirm = materialSymbols.checkCircle;
  revise = materialSymbols.refresh;

  userVoteOption : any;
  userVoteSubmittedDate: string | undefined;

  private unsubscribe$ = new Subject<void>();

  get ballotNoteCtrl() {
    return this.ballotNoteFormGroup.get('ballotNoteCtrl');
  }

  constructor(
    public _authService: AuthService,
    private _srsBallotService: SrsBallotService,
    private _documentService: DocumentService,
    private _samlService: SamlService,
    private _modalService: NgbModal,
    private _activeRouter: ActivatedRoute,
    private _router: Router,
    private dateFormatPipe: DateFormatPipe
    )
    {}

  ngOnInit(){
    this.ballotId = this.srsBallotDetail?.ballotId || '';
    this.ballotResolution = this.srsBallotDetail?.ballotResolution || '';
    this.designation = this.srsBallotDetail?.designation || '';
    this.designationURL = this.srsBallotDetail?.designationURL || '';
    this.checkUserPermission();
    this.setupUpdateBallotNoteSubscription();
    this.loadBallotNote(this.srsBallotDetail?.notes || null);
    this.checkDatesEditableStatus();
    this.detectBallotStartDateChanges();
    this.detectBallotEndDateChanges();
    this.setViewRedirection();
    this.initVoteButton();
  }

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['srsBallotDetail'] && changes['srsBallotDetail'].currentValue) {
        const ballotDetail = changes['srsBallotDetail'].currentValue;
        this.loadBallotNote(ballotDetail?.notes || null);
        this.ballotStatusId = ballotDetail?.unifiedStatusId || UnifiedBallotStatus.created;
      }
  }


  setViewRedirection() {
    const voteView = this._activeRouter?.snapshot?.queryParamMap?.get(SummaryComponentView.vote);
    if (voteView) {
        this.openVotePopup = true;
        urlUtility.removeQueryParam(this._router, SummaryComponentView.vote);
    }
  }

  redirectToVoteIfRequired() {

    if (this.openVotePopup && this.userVote) {
      this.openVotePopup = false;
      this.openPlaceVoteModal();
    }
  }

  initializeBallotDates(scheduledBallot = false) {
    const today = new Date();
    this.ballotStartDate = this.srsBallotDetail?.ballotStartDate || undefined;
    this.ballotEndDate = this.srsBallotDetail?.ballotEndDate || undefined;
    if(scheduledBallot && this.ballotStartDate && this.ballotEndDate){
      this.ballotSummaryDateForm.patchValue({
        ballotStartDateCtrl: this.ballotStartDate,
        ballotEndDateCtrl: this.ballotEndDate
      });
      this.ballotStartMinDate = new Date(today);
      this.ballotStartMaxDate = new Date(this.ballotEndDate);
      this.ballotStartMaxDate.setDate(this.ballotStartMaxDate.getDate() - 1);
      this.ballotEndMinDate = new Date(this.ballotStartDate);
      this.ballotEndMinDate.setDate(this.ballotEndMinDate.getDate() + 1);
    }
    if(!scheduledBallot && this.ballotStartDate && this.ballotEndDate){
      this.ballotSummaryDateForm.patchValue({
        ballotEndDateCtrl: this.ballotEndDate
      });

      this.ballotEndMinDate = new Date(today);
      this.ballotEndMinDate.setDate(today.getDate() - 1);
    }
  }

  checkUserPermission() {
    this._authService.currentUserSubject.pipe(
      filter(user => !!user),
      take(1)
    )
    .subscribe((user) => {
      this.currentUser = user;
      if (this.currentUser) {
        this.isUserPmOrAbove = UserUtility.userIsPmOrAbove(this.currentUser);
        this.isInternalPublicUser = UserUtility.isInternalPublicUser(this.currentUser);
        if(this.isUserPmOrAbove){
          this.isAuthorized = true;
        } else {
          this._samlService.getLoggedInUserHoSDStatus().subscribe((response) => {
            this.isAuthorized = response.content;
          });
        }
      }
    })
  }



  checkDatesEditableStatus(){
    const status = this.srsBallotDetail?.status || '';
    if(status && status != '' && this.isAuthorized){
      if((status == SRSBallotStatus.Scheduled) && true){
        this.isBallotStartDateEditable = true;
        this.isBallotEndDateEditable = true;
        this.initializeBallotDates(true);
      }
      if(status == SRSBallotStatus.OpenForVoting){
        this.isBallotEndDateEditable = true;
        this.initializeBallotDates();
      }
    }
  }

  updateBallotDates(): void{
  this.ballotId = this.srsBallotDetail?.ballotId || '';
  if (this.ballotId && this.ballotId.trim() !== '') {
      this.srsBallotUpdateDatesRequest = {
        ...this.srsBallotUpdateDatesRequest,
        ballotId: this.ballotId,
        startDate: this.ballotStartDate,
        endDate: this.ballotEndDate
      };
      this.updateBallotDetail(this.ballotId);
    }
  }

  updateBallotDetail(ballotId: string): void {
    this._srsBallotService.updateBallotDates(ballotId, this.srsBallotUpdateDatesRequest).pipe(
      takeUntil(this.unsubscribe$)
    )
    .subscribe({
      next: (response) => {
        if(response.success) {
          console.log('Ballot details updated successfully:', response);
        }
      },
      error: (error) => {
        console.error('Error updating ballot details:', error);
      }
  });
  }

  setupUpdateBallotNoteSubscription(): void {
    this.ballotNoteFormGroup.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(1000),
      distinctUntilChanged(
        (prev, curr) => prev.ballotNoteCtrl === curr.ballotNoteCtrl
      ),
      switchMap((formData: Partial<INotesFormData>) =>
        this.updateBallotNote(formData)
      )
    )
    .subscribe({
      next: (apiResponse) => {
        if (!apiResponse || !apiResponse.success) {
          console.log(
            `Failed to save SR ballot note. Error: ${apiResponse?.message}`
          );
        }
      },
      error: (error) => {
        console.log(`Error saving SR ballot notes: ${error}`);
      },
    });
  }

  updateBallotNote(
    formValue: Partial<INotesFormData>
  ): Observable<IApiResponse<string>> {
    var request: ISrUpdateNoteRequest = {
      ballotId: this.srsBallotDetail?.ballotId || '',
      notes:
        !formValue.ballotNoteCtrl || formValue.ballotNoteCtrl === ''
          ? null
          : formValue.ballotNoteCtrl,
    };

    var obs$ = this._srsBallotService.updateBallotNote(request);
    return obs$;
  }

  loadBallotNote(ballotNote: string | null): void {
    this.ballotNoteFormGroup.patchValue(
      {
        ballotNoteCtrl: ballotNote,
      },
      { emitEvent: false } // Don't trigger the form's valueChanges event
    );
  }

  getCommitteeName(){
    return `${this.srsBallotDetail?.committee} ${this.srsBallotDetail?.committeeTitle} `;
  }


  downloadDocument(e: MouseEvent, uri: string) {
      e.stopPropagation();
      const fileName = uri + `.pdf`;

      if (!uri || !fileName) {
        console.log('No URI found for document.');
        return;
      }

      this._documentService.downloadDocument(uri).subscribe(response => {
        if (response.success) {
          FileDownloadUtility.downloadbase64StringToBrowser(response.content, fileName);
        } else {
          console.log(`Download failed. ${response.message}`);
        }
      });

    }

    detectBallotStartDateChanges(): void {
      this.ballotSummaryDateForm.get('ballotStartDateCtrl')?.valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((selectedEndDate) => {
          if(selectedEndDate) {
            this.ballotEndMinDate = new Date(selectedEndDate);
            this.ballotEndMinDate.setDate(this.ballotEndMinDate.getDate() + 1);
            this.ballotStartDate = selectedEndDate;
            this.updateBallotDates();
          }
        });
    }

    detectBallotEndDateChanges(): void {
      this.ballotSummaryDateForm.get('ballotEndDateCtrl')?.valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((selectedEndDate) => {
          if(selectedEndDate) {
            this.ballotStartMaxDate = new Date(selectedEndDate);
            this.ballotStartMaxDate.setDate(this.ballotStartMaxDate.getDate() - 1);
            this.ballotEndDate = selectedEndDate;
            this.updateBallotDates();
          }
        });
    }

    ngOnDestroy(): void {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
    }

    initVoteButton() {
      this.userVote = this.srsBallotDetail?.votes.find(vote => vote.email == this.currentUser?.email);

      this.redirectToVoteIfRequired();

      if (!this.userVote) return;

      this.hasVoted = this.userVote?.voteTypeId != SrsVoteId.SVT_NONE;
      this.isVoteButtonEnable = this.srsBallotDetail?.statusId == SrsBallotStatusId.PSB_OPENED;
      this.userVoteOption = srsVoteOption[this.userVote?.voteTypeId??""];
      this.userVoteSubmittedDate = this.dateFormatPipe.transform(this.userVote?.lastUpdated);
      this.isVotingActionsVisible = this.userVote?.isCurrentUserEligibleVoter??false;
    }

    refreshVoteSection(voteOption: SrsVoteId) {
      this.hasVoted = true;
      this.userVoteOption = srsVoteOption[voteOption];
      this.userVoteSubmittedDate = this.dateFormatPipe.transform(new Date());
    }

    openPlaceVoteModal() {

      const modalRef = this._modalService.open(SrsPlaceVoteComponent, { windowClass: 'place-vote-custom-modal'});
      modalRef.componentInstance.ballot = this.srsBallotDetail;
      modalRef.componentInstance.user = this.currentUser;

      modalRef.closed.pipe(
        take(1),
      ).subscribe((vote) => {
        if (vote && Object.values(SrsVoteId).includes(vote)) {
          this.refreshVoteSection(vote);
          this.onPlaceVote?.emit(this.srsBallotDetail?.ballotId);
        }
      });
    }

    openBallotDecisionGuidanceModal() {
      const guidanceModalRef = this._modalService.open(SrsBallotGuidanceComponent, {centered: true});
    }

    onBallotSaved($event: string) {
      this.afterSave.emit($event);
    }

}

interface INotesFormData {
  ballotNoteCtrl: string | null;
}
